import type { TableFilterData } from '@/types/Base';
import type { MaterialLastVisitValue } from '@/types/Materials';
import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getMaterialLastVisitValuesWithConditionsSchema } from '@/types/schemas/priceCollection/getMaterialLastVisitValuesWithConditions';
import { getPriceCollectionDocumentsWithConditionsSchema } from '@/types/schemas/priceCollection/getPriceCollectionDocumentsWithConditions';
import type {
  PriceCollectionDocument,
  PriceCollectionDocumentDetails,
} from '@/types/PriceCollectionDocuments';
import { getPriceCollectionDocumentDetailsSchema } from '@/types/schemas/priceCollection/getPriceCollectionDocumentDetailsWithConditions';

export async function getMaterialLastVisitValuesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  isActive,
}: TableFilterData): Promise<{ data: MaterialLastVisitValue[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/pricecollection/last-visit-values/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      isActive: isActive,
    }
  );
  const valid = validate(getMaterialLastVisitValuesWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getPriceCollectionDocumentsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  isActive,
}: TableFilterData): Promise<{ data: PriceCollectionDocument[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/pricecollection/documents/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      isActive: isActive,
    }
  );
  const valid = validate(getPriceCollectionDocumentsWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getPriceCollectionDocumentById(
  id: string
): Promise<PriceCollectionDocument | null> {
  const response = await axios.get(`/v2/pricecollection/documents/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const document = validate(getPriceCollectionDocumentsWithConditionsSchema, [response.data]);
  return document[0];
}

export async function getPriceCollectionDocumentDetailsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  documentId = null,
}: TableFilterData & { documentId?: string | null }): Promise<{
  data: PriceCollectionDocumentDetails[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  let url = `/v2/pricecollection/document-details/search?take=${itemsPerPage}&skip=${skip}`;
  if (documentId != null) url += `&documentId=${documentId}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    documentId,
  });

  const valid = validate(getPriceCollectionDocumentDetailsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}
